import styles from "./main.module.scss";
import { ReactComponent as Logo } from "../../assets/James-Hope-University-Logo.svg";
import Stepper from "../../components/stepper/stepper";
import {
  Institution,
  PersonalInfo,
  Qualification,
  Review,
  WorkHistory,
} from "../../components/onboardingSteps";
import { useAtom, useAtomValue } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { useQuery } from "@tanstack/react-query";
import { getAllDisplayTab } from "../../utils/request";
import { logout } from "../../utils/logout";
import { useApplicationPeriod } from "../../hooks/useApplicationPeriod";
import ProgramInfo from "../../components/programInfo";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routes";

const Onboarding = () => {
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const user = useAtomValue(userAtom);
  const { isApplicationPeriodActive } = useApplicationPeriod();
  const navigate = useNavigate();

  const tabParams = {
    sessionId: user?.sessionId ?? 0,
    batchName: user?.applicationName ?? "",
    programType: user?.programTypeId ?? 0,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-display-tab"],
    queryFn: () => getAllDisplayTab(tabParams),
  });

  useEffect(() => {
    if (user?.isPaymentSubmitted) {
      navigate(routes.dashboard.dashboard);
    }
  }, []);

  useEffect(() => {
    const firstIncompleteTab = findFirstIncompleteTab();
    if (firstIncompleteTab !== pageIndex) {
      // Update pageIndexAtom to first incomplete tab
      setPageIndex(firstIncompleteTab);
    }
  }, [data]);

  const findFirstIncompleteTab = () => {
    if (!data?.data) return 1;

    for (const tab of data.data) {
      switch (tab.tabName.trim()) {
        case "Personal Information":
          if (user?.isPersonalInfoSubmitted === false) return tab.tabNumber;
          break;
        case "Work Experience":
          if (user?.isworkhistorySubmitted === false) return tab.tabNumber;
          break;
        case "Institution":
          if (user?.iInstitutionSubmitted === false) return tab.tabNumber;
          break;
        case "Qualification":
          if (user?.isqualificationSubmitted === false) return tab.tabNumber;
          break;
        default:
          break;
      }
    }
    return data.data.length; // All tabs are complete, return last tab
  };

  // Define a type for the allowed tab names
  type TabNames =
    | "Personal Information"
    | "Work Experience"
    | "Institutions"
    | "Qualifications"
    | "Review";

  // Map each tab name to the corresponding component with type annotations
  const tabComponents: Record<TabNames, JSX.Element> = {
    "Personal Information": <PersonalInfo onClose={() => {}} />,
    "Work Experience": <WorkHistory />,
    Institutions: <Institution />,
    Qualifications: <Qualification />,
    Review: <Review />,
  };

  // Type guard to check if a value is a valid TabName
  const isValidTabName = (tabName: string): tabName is TabNames => {
    return tabName in tabComponents;
  };

  // Dynamically render component based on pageIndex and fetched data
  const steps = () => {
    if (isLoading) return <p>Loading...</p>;
    if (isError) return <p>Error: {error.message}</p>;

    const currentTab = data?.data.find((tab) => tab.tabNumber === pageIndex);

    if (currentTab && isValidTabName(currentTab.tabName.trim())) {
      return tabComponents[currentTab.tabName.trim() as TabNames];
    }
    return <PersonalInfo onClose={() => {}} />;
  };

  const displaySteps =
    data?.data
      ?.sort((a, b) => a.tabNumber - b.tabNumber)
      ?.map((item) => {
        return item?.tabName;
      }) ?? [];

  const isActiveApplicationPeriod = isApplicationPeriodActive();

  if (!isActiveApplicationPeriod) {
    return (
      <main className={styles.onboarding}>
        <nav className={styles.nav}>
          <Logo />
        </nav>
        <button onClick={logout} className={styles.logout}>
          Logout
        </button>
        <hr className={styles.divider} />
        <br />

        <div className={styles.card}>
          <h2>Application Period Inactive</h2>
          <p>
            The application period is currently inactive. Please check back
            later.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.onboarding}>
      <nav className={styles.nav}>
        <Logo />
      </nav>
      <button onClick={logout} className={styles.logout}>
        Logout
      </button>
      <hr className={styles.divider} />
      <br />
      <ProgramInfo className={styles.programInfo} />
      <br />
      <Stepper
        titles={displaySteps}
        noOfSteps={displaySteps?.length}
        currentStep={Number(pageIndex)}
      />
      <br />
      <div className={styles.card}>
        {steps()}
        <br />
      </div>
    </main>
  );
};

export default Onboarding;
